import React, { useState, useCallback } from 'react';
import { Box, Chip, Typography, IconButton, Tooltip } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import { GridRenderCellParams, GridRowId } from '@mui/x-data-grid';

import PaginatedDataGrid, {
  PaginatedDataGridColumn,
  PaginatedDataGridAction
} from 'src/components/paginated-data-grid/PaginatedDataGrid';

import SimpleConfirmationDialog from 'src/components/SimpleConfirmationDialog';
import RentCycleDialog from './RentCycleDialog';
import { DataService } from 'src/services/data-service';
import { useAppDispatch } from 'src/redux/hooks';
import { showAlert } from 'src/redux/alert/alertReducer';

interface RentCycle {
  id: string;
  name: string;
  description: string;
  active: boolean;
}

const RentCyclesListing: React.FC = () => {
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedRentCycle, setSelectedRentCycle] = useState<RentCycle | null>(null);
  const [toggleLoading, setToggleLoading] = useState<string | null>(null);
  const dispatch = useAppDispatch();

  // ---------- Delete Rent Cycle ----------
  const handleDeleteClick = (id: GridRowId) => {
    setDeleteId(String(id));
  };

  const handleDeleteConfirm = async () => {
    if (!deleteId) return;

    try {
      const response = await DataService.delete(`api/rent-cycles/${deleteId}`, {});
      if (response.ok) {
        setRefreshKey((prev) => prev + 1);
        dispatch(
          showAlert({
            message: 'Rent cycle deleted successfully!',
            type: 'success'
          })
        );
      } else {
        throw new Error('Failed to delete rent cycle');
      }
    } catch (error) {
      dispatch(
        showAlert({
          message: 'Failed to delete rent cycle. Please try again.',
          type: 'error'
        })
      );
    } finally {
      setDeleteId(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteId(null);
  };

  // ---------- Edit Rent Cycle ----------
  const handleEditClick = (rentCycle: RentCycle) => {
    setSelectedRentCycle(rentCycle);
    setEditDialogOpen(true);
  };

  // ---------- Add Rent Cycle ----------
  const handleAddRentCycle = () => {
    setSelectedRentCycle(null);
    setEditDialogOpen(true);
  };

  // ---------- Toggle Active/Inactive ----------
  const handleToggleActive = async (rentCycle: RentCycle) => {
    setToggleLoading(rentCycle.id);

    try {
      const endpoint = rentCycle.active
        ? `api/rent-cycles/deactivate?id=${rentCycle.id}`
        : `api/rent-cycles/activate?id=${rentCycle.id}`;

      const response = await DataService.patch(endpoint, {});

      if (response.ok) {
        setRefreshKey((prev) => prev + 1);
        dispatch(
          showAlert({
            message: `Rent cycle ${rentCycle.active ? 'deactivated' : 'activated'} successfully!`,
            type: 'success'
          })
        );
      } else {
        throw new Error('Failed to update rent cycle status');
      }
    } catch (error) {
      dispatch(
        showAlert({
          message: 'Failed to update rent cycle status. Please try again.',
          type: 'error'
        })
      );
    } finally {
      setToggleLoading(null);
    }
  };

  // ---------- Handle Dialog Success ----------
  const handleDialogSuccess = () => {
    setEditDialogOpen(false);
    setRefreshKey((prev) => prev + 1);
    dispatch(
      showAlert({
        message: selectedRentCycle ? 'Rent cycle updated successfully!' : 'Rent cycle created successfully!',
        type: 'success'
      })
    );
  };

  // ---------- Data Grid Columns ----------
  const columns: PaginatedDataGridColumn[] = [
    {
      field: 'name',
      headerName: 'Rent Cycle',
      flex: 1.5,
      minWidth: 180,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AccessTimeIcon color="primary" fontSize="small" />
          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
            {params.value}
          </Typography>
        </Box>
      )
    },
    {
      field: 'description',
      headerName: 'Description',
      flex: 2,
      minWidth: 200,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {params.value || (
            <span style={{ fontStyle: 'italic' }}>
              No description provided
            </span>
          )}
        </Typography>
      )
    },
    {
      field: 'active',
      headerName: 'Status',
      width: 120,
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={params.value ? 'Active' : 'Inactive'}
          color={params.value ? 'success' : 'default'}
          size="small"
          variant="outlined"
        />
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Tooltip title={params.row.active ? 'Deactivate' : 'Activate'}>
            <IconButton
              size="small"
              onClick={() => handleToggleActive(params.row)}
              disabled={toggleLoading === params.row.id}
              color={params.row.active ? 'success' : 'default'}
            >
              {params.row.active ? <ToggleOnIcon /> : <ToggleOffIcon />}
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit">
            <IconButton
              size="small"
              onClick={() => handleEditClick(params.row)}
              color="primary"
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              size="small"
              onClick={() => handleDeleteClick(params.row.id)}
              color="error"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )
    }
  ];

  // ---------- Data Grid Actions (Toolbar) ----------
  const actions: PaginatedDataGridAction[] = [
    {
      key: 'add',
      title: 'Add Rent Cycle',
      icon: <AddIcon />,
      onClick: handleAddRentCycle,
      disabled: () => false,
      color: 'primary'
    }
  ];

  // ---------- Fetch Rent Cycles Function ----------
  const fetchRentCycles = useCallback(
    async (params: {
      page: number;
      pageSize: number;
      sortModel?: any;
      filterModel?: any;
      search?: string;
    }) => {
      console.log('fetchRentCycles called with params:', params);
      try {
        const response = await DataService.get('api/rent-cycles/all');

        if (response.ok) {
          const responseData = await response.json();
          console.log('API Response:', responseData);

          let rawRentCycles: any[] = [];

          if (responseData.success && responseData.data && Array.isArray(responseData.data)) {
            rawRentCycles = responseData.data;
          } else if (Array.isArray(responseData)) {
            rawRentCycles = responseData;
          } else {
            console.error('Unexpected response structure:', responseData);
            throw new Error('Invalid response structure');
          }

          const rentCycles: RentCycle[] = rawRentCycles.map(rentCycle => ({
            id: rentCycle.id,
            name: rentCycle.name,
            description: rentCycle.description,
            active: rentCycle.active
          }));

          console.log('Raw rent cycles from API:', rawRentCycles);
          console.log('Mapped rent cycles:', rentCycles);

          // Filter by search term if provided
          let filteredRentCycles = rentCycles;
          if (params.search) {
            const searchLower = params.search.toLowerCase();
            filteredRentCycles = rentCycles.filter(
              rentCycle =>
                rentCycle.name.toLowerCase().includes(searchLower) ||
                (rentCycle.description && rentCycle.description.toLowerCase().includes(searchLower))
            );
          }

          // Apply sorting if provided
          if (params.sortModel && params.sortModel.length > 0) {
            const sortModel = params.sortModel[0];
            filteredRentCycles.sort((a, b) => {
              let aValue: any = a[sortModel.field as keyof RentCycle];
              let bValue: any = b[sortModel.field as keyof RentCycle];

              // Handle string comparison
              if (typeof aValue === 'string') {
                aValue = aValue.toLowerCase();
                bValue = (bValue as string).toLowerCase();
              }

              if (sortModel.sort === 'desc') {
                return aValue < bValue ? 1 : -1;
              }
              return aValue > bValue ? 1 : -1;
            });
          }

          // Apply pagination (DataGrid uses 0-based page indexing)
          const startIndex = params.page * params.pageSize;
          const paginatedRentCycles = filteredRentCycles.slice(startIndex, startIndex + params.pageSize);

          const totalPages = Math.ceil(filteredRentCycles.length / params.pageSize);

          const result = {
            items: paginatedRentCycles,
            totalCount: filteredRentCycles.length,
            pageNumber: params.page + 1,
            pageSize: params.pageSize,
            totalPages: totalPages,
            hasNext: params.page + 1 < totalPages,
            hasPrevious: params.page > 0
          };

          console.log('Final result being returned:', result);
          return result;
        } else {
          throw new Error(`Failed to fetch rent cycles: ${response.status} ${response.statusText}`);
        }
      } catch (error) {
        console.error('Error fetching rent cycles:', error);
        const errorMessage = error instanceof Error ? error.message : 'Failed to load rent cycles. Please try again.';
        dispatch(
          showAlert({
            message: errorMessage,
            type: 'error'
          })
        );
        return {
          items: [],
          totalCount: 0,
          pageNumber: params.page + 1,
          pageSize: params.pageSize,
          totalPages: 0,
          hasNext: false,
          hasPrevious: false
        };
      }
    },
    [dispatch]
  );

  return (
    <Box sx={{ width: '100%', height: '100%' }}>
      <Typography variant="h5" sx={{ mb: 1.5, fontWeight: 'bold' }}>
        Rent Cycles Management
      </Typography>

      <PaginatedDataGrid
        key={refreshKey}
        title="Rent Cycles"
        columns={columns}
        useBackendPagination
        onDataFetch={fetchRentCycles}
        actions={actions}
        emptyMessage="No rent cycles found"
      />

      {/* Delete Confirmation Dialog */}
      <SimpleConfirmationDialog
        open={!!deleteId}
        title="Delete Rent Cycle"
        message="Are you sure you want to delete this rent cycle? This action cannot be undone."
        onConfirm={handleDeleteConfirm}
        onClose={handleDeleteCancel}
      />

      {/* Add/Edit Rent Cycle Dialog */}
      <RentCycleDialog
        open={editDialogOpen}
        rentCycle={selectedRentCycle}
        onClose={() => setEditDialogOpen(false)}
        onSuccess={handleDialogSuccess}
      />
    </Box>
  );
};

export default RentCyclesListing;